import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy, ExternalLink, ImageUp, Loader2, Plus, QrCode, TriangleAlert, Undo2, X } from 'lucide-react';

import { dataApi } from '../../services/api';
import { contrastRatio } from '../../lib/contrast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import { Tooltip } from '../ui/tooltip';
import QuoteFormPreview, { PREVIEW_DEFAULTS } from './QuoteFormPreview';
import QuoteFormQrDialog from './QuoteFormQrDialog';
import { cn } from '../../lib/utils';

/**
 * Settings for the public quote request form.
 *
 * Changes are staged and previewed, then saved together — a shop trying out a
 * colour should not be publishing each keystroke to its live customer form.
 *
 * There is deliberately almost no explanatory copy: the preview beside the
 * controls shows what every colour does, and highlights the affected parts of
 * the form while you are editing one.
 */

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/** Team prefs are one JSON blob with a size ceiling; the server caps this too. */
const MAX_SERVICES = 20;

const COLOURS = [
  { name: 'accent', label: 'Accent' },
  { name: 'accentText', label: 'On accent' },
  { name: 'background', label: 'Page' },
  { name: 'text', label: 'Text' }
];

/** Pairs that have to stay legible, and which chip to flag when they don't. */
const CONTRAST_PAIRS = [
  { on: 'accentText', over: 'accent' },
  { on: 'text', over: 'background' }
];

const normaliseHex = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9a-f]{3,8}$/i.test(withHash) ? withHash.toLowerCase() : '';
};

/**
 * `<input type="color">` accepts only #rrggbb — a shorthand or an alpha value
 * makes it silently fall back to black, which would read as "your colour was
 * thrown away". Expand shorthand and drop alpha for the swatch only; the text
 * field keeps whatever was typed.
 */
const toSwatchValue = (hex) => {
  const value = hex.replace('#', '');
  if (value.length === 3) return `#${value.split('').map((c) => c + c).join('')}`;
  if (value.length >= 6) return `#${value.slice(0, 6)}`;
  return '';
};

const ColourChip = ({ colour, value, resolved, warning, onChange, onActivate, onClear }) => (
  <div
    className="group relative flex items-center gap-3 rounded-lg bg-muted/40 p-2.5 transition-colors focus-within:bg-muted"
    onMouseEnter={() => onActivate(colour.name)}
    onMouseLeave={() => onActivate(null)}
  >
    <div className="relative h-9 w-9 shrink-0">
      <span
        className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-inset ring-black/10"
        style={{ backgroundColor: resolved }}
      />
      <input
        type="color"
        aria-label={colour.label}
        value={resolved}
        onFocus={() => onActivate(colour.name)}
        onBlur={() => onActivate(null)}
        onChange={(event) => onChange(event.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </div>

    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium">{colour.label}</span>
        {warning && (
          <Tooltip content={`Hard to read — ${warning}`}>
            <TriangleAlert className="h-3.5 w-3.5 text-amber-500" />
          </Tooltip>
        )}
      </div>

      <Input
        value={value}
        placeholder={PREVIEW_DEFAULTS[colour.name]}
        onFocus={() => onActivate(colour.name)}
        onBlur={() => onActivate(null)}
        onChange={(event) => onChange(event.target.value)}
        className="mt-0.5 h-6 border-0 bg-transparent px-0 font-mono text-xs shadow-none focus-visible:ring-0"
      />
    </div>

    {value && (
      <button
        type="button"
        onClick={onClear}
        aria-label={`Reset ${colour.label}`}
        className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
      >
        <Undo2 className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

const QuoteFormSettings = () => {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [logo, setLogo] = useState({ file: null, url: '', cleared: false });
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await dataApi.intakeSettingsGet();
      setSaved(data);
      setDraft({
        acceptingQuotes: data.acceptingQuotes,
        colours: { ...data.colours },
        services: (data.services || []).map((service) => ({ ...service }))
      });
      setLogo({ file: null, url: '', cleared: false });
    } catch (err) {
      toast.error(err.message || 'Could not load the quote form settings');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Object URLs for the staged logo have to be released or they leak.
  useEffect(() => () => { if (logo.url) URL.revokeObjectURL(logo.url); }, [logo.url]);

  const dirty = useMemo(() => {
    if (!saved || !draft) return false;
    return (
      draft.acceptingQuotes !== saved.acceptingQuotes
      || COLOURS.some((colour) => (draft.colours[colour.name] || '') !== (saved.colours[colour.name] || ''))
      || JSON.stringify(draft.services) !== JSON.stringify(saved.services || [])
      || Boolean(logo.file)
      || logo.cleared
    );
  }, [saved, draft, logo]);

  const previewLogo = logo.cleared ? '' : (logo.url || saved?.logoUrl || '');

  const warnings = useMemo(() => {
    if (!draft) return {};

    const found = {};
    for (const pair of CONTRAST_PAIRS) {
      const on = normaliseHex(draft.colours[pair.on]) || PREVIEW_DEFAULTS[pair.on];
      const over = normaliseHex(draft.colours[pair.over]) || PREVIEW_DEFAULTS[pair.over];
      const ratio = contrastRatio(on, over);

      if (ratio !== null && ratio < 4.5) {
        const message = `${ratio.toFixed(1)}:1 against ${COLOURS.find((c) => c.name === pair.over).label.toLowerCase()}`;
        found[pair.on] = message;
      }
    }
    return found;
  }, [draft]);

  const setColour = (name, value) => {
    setDraft((current) => ({ ...current, colours: { ...current.colours, [name]: value } }));
  };

  const updateService = (index, patch) => {
    setDraft((current) => ({
      ...current,
      services: current.services.map((service, i) => (i === index ? { ...service, ...patch } : service))
    }));
  };

  const handleLogoFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Logos must be under 2MB');
      return;
    }

    if (logo.url) URL.revokeObjectURL(logo.url);
    setLogo({ file, url: URL.createObjectURL(file), cleared: false });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const colours = {};
      for (const colour of COLOURS) {
        colours[colour.name] = normaliseHex(draft.colours[colour.name]);
      }

      await dataApi.intakeSettingsSave({
        acceptingQuotes: draft.acceptingQuotes,
        colours,
        services: draft.services,
        logoFile: logo.file,
        removeLogo: logo.cleared
      });

      await load();
      toast.success('Quote form updated');
    } catch (err) {
      toast.error(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (logo.url) URL.revokeObjectURL(logo.url);
    setDraft({
      acceptingQuotes: saved.acceptingQuotes,
      colours: { ...saved.colours },
      services: (saved.services || []).map((service) => ({ ...service }))
    });
    setLogo({ file: null, url: '', cleared: false });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(saved.formUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Could not copy the link');
    }
  };

  if (!saved || !draft) return <Skeleton className="h-96 w-full" />;

  const linkReady = saved.formUrl && !saved.formUrl.startsWith('/');

  return (
    <div className="space-y-6">
      {/* Whether the form is open at all comes first — it is the setting most
          likely to be reached for, and it governs everything below it. */}
      <div className="flex items-center justify-between gap-4 rounded-lg bg-muted/40 px-3 py-2.5">
        <Label htmlFor="accepting-quotes" className="cursor-pointer">Accepting requests</Label>
        <Switch
          id="accepting-quotes"
          checked={draft.acceptingQuotes}
          onCheckedChange={(checked) => setDraft((current) => ({ ...current, acceptingQuotes: checked }))}
        />
      </div>

      {/* The link a shop gives its customers */}
      {linkReady ? (
        <div className="space-y-2">
          <Label>Customer link</Label>
          <div className="flex gap-2">
            <Input readOnly value={saved.formUrl} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" size="icon" onClick={handleCopy} aria-label="Copy link">
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setQrOpen(true)}
              aria-label="Show QR code"
            >
              <QrCode className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" asChild aria-label="Open form">
              <a href={saved.formUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a>
            </Button>
          </div>
        </div>
      ) : (
        <p className="rounded-lg bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground">
          Set <code className="text-xs">REACT_APP_CUSTOMER_FORM_URL</code> at build time to show your link here.
        </p>
      )}

      <QuoteFormQrDialog
        open={qrOpen}
        onOpenChange={setQrOpen}
        url={saved.formUrl}
        shopName={saved.shopName}
      />

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
        {/* Controls */}
        <div className="order-2 space-y-6 lg:order-1">
          <div className="space-y-3">
            <Label>Logo</Label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'flex h-14 w-32 items-center justify-center rounded-lg bg-muted/50 transition-colors hover:bg-muted',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
              >
                {previewLogo ? (
                  <img src={previewLogo} alt="Your logo" className="max-h-12 max-w-28 object-contain" />
                ) : (
                  <ImageUp className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {previewLogo && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove logo"
                  onClick={() => {
                    if (logo.url) URL.revokeObjectURL(logo.url);
                    setLogo({ file: null, url: '', cleared: true });
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                onChange={handleLogoFile}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Colours</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {COLOURS.map((colour) => (
                <ColourChip
                  key={colour.name}
                  colour={colour}
                  value={draft.colours[colour.name] || ''}
                  resolved={
                    toSwatchValue(normaliseHex(draft.colours[colour.name]))
                    || PREVIEW_DEFAULTS[colour.name]
                  }
                  warning={warnings[colour.name]}
                  onActivate={setActive}
                  onChange={(value) => setColour(colour.name, value)}
                  onClear={() => setColour(colour.name, '')}
                />
              ))}
            </div>
          </div>


          {/* Optional extras offered on the "While it's in the workshop" step.
              No defaults: every shop prices these differently, and a shop with
              none configured simply isn't asked. */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Optional extras</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={draft.services.length >= MAX_SERVICES}
                onClick={() => setDraft((current) => ({
                  ...current,
                  services: [...current.services, dataApi.newIntakeService()]
                }))}
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>

            {draft.services.length === 0 ? (
              <p className="rounded-lg bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
                None — customers won't be asked.
              </p>
            ) : (
              <div className="space-y-2">
                {draft.services.map((service, index) => (
                  <div key={service.id} className="flex items-center gap-2">
                    <Input
                      value={service.label}
                      placeholder="Full vehicle cut and polish"
                      onChange={(event) => updateService(index, { label: event.target.value })}
                      className="flex-1"
                    />
                    <Input
                      value={service.price}
                      placeholder="from $200"
                      onChange={(event) => updateService(index, { price: event.target.value })}
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${service.label || 'extra'}`}
                      onClick={() => setDraft((current) => ({
                        ...current,
                        services: current.services.filter((_, i) => i !== index)
                      }))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="order-1 lg:order-2">
          <div className="lg:sticky lg:top-4">
            <QuoteFormPreview
              colours={draft.colours}
              logoUrl={previewLogo}
              shopName={saved.shopName}
              accepting={draft.acceptingQuotes}
              highlight={active}
            />
          </div>
        </div>
      </div>

      {/* Nothing is published until this is used */}
      {dirty && (
        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-black/5 bg-background/95 py-3 backdrop-blur">
          <Button type="button" variant="ghost" onClick={handleDiscard} disabled={saving}>
            Discard
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default QuoteFormSettings;
